import { Instant, ZoneId } from '@js-joda/core';
import '@js-joda/timezone';
import { Select } from '@neo4j-ndl/react';
import { uniq } from 'lodash-es';
import { useMemo } from 'react';

type Option = {
  label: string;
  value: string;
};

const zoneIds = ZoneId.getAvailableZoneIds().sort();

const timeOffsets = uniq(
  zoneIds
    .map((zoneId) => {
      return ZoneId.of(zoneId).rules().offset(Instant.now());
    })
    .sort((a, b) => b.compareTo(a)),
);

// this is a list of zones not supported by db
const zoneIdBlacklist = ['HST', 'EST', 'MST', 'Pacific/Kanton', 'ROC'];

const zoneIdOptions: Option[] = zoneIds
  .filter((zoneId) => !zoneId.startsWith('Etc/') && !zoneId.startsWith('GMT') && !zoneIdBlacklist.includes(zoneId))
  .map((zoneId) => ({
    value: zoneId,
    label: zoneId,
  }));

const timeOffsetOptions: Option[] = timeOffsets.map((timeOffset) => ({
  value: timeOffset.toString(),
  label: timeOffset.toString() === 'Z' ? 'Z' : `GMT${timeOffset.toString()}`,
}));

const allOptions = [...zoneIdOptions, ...timeOffsetOptions];

const valueToOption = (value: Option['value']): Option => {
  const option = allOptions.find((o) => o.value === value);
  return (
    option ?? {
      value,
      label: value,
    }
  );
};

interface TimezonePickerProps {
  timeZone: Option['value'];
  className?: string;
  disabled?: boolean;
  includeTimeZone?: boolean;
  selectProps?: Omit<React.ComponentProps<typeof Select>, 'type' | 'selectProps'>;
  onChange: (value: Option['value']) => void;
}

export const TimezonePicker = ({
  timeZone,
  className,
  includeTimeZone,
  disabled,
  selectProps,
  onChange,
}: TimezonePickerProps) => {
  const groupedOptions: { label: string; options: Option[] }[] = useMemo(() => {
    const options = [
      {
        label: 'Change time offset',
        options: timeOffsetOptions,
      },
    ];
    if (includeTimeZone === true) {
      options.push({
        label: 'Change time zone',
        options: zoneIdOptions,
      });
    }
    return options;
  }, [includeTimeZone]);

  const selectedOption = useMemo(() => valueToOption(timeZone), [timeZone]);

  return (
    <Select
      className={className}
      size="small"
      type="select"
      isFluid
      isDisabled={disabled}
      selectProps={{
        value: selectedOption,
        options: groupedOptions,
        onChange: (newValue: unknown) => {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const option = newValue as Option | null;
          if (option !== null) {
            onChange(option.value);
          }
        },
        menuPortalTarget: document.querySelector('body'),
        styles: {
          menuPortal: (baseStyles) => ({
            ...baseStyles,
            // Override for opening the menu above the legend popup
            zIndex: 70,
          }),
        },
      }}
      {...selectProps}
    />
  );
};
